import React, { useEffect } from "react";
import "./index.scss";

/**
 * DocumentViewerModal
 *
 * Shows a document (PDF, image) in an inline modal.
 * Fullscreen icon (bottom-right) opens the doc in a new tab.
 *
 * Props:
 *  isOpen       - bool
 *  onClose      - () => void
 *  url          - blob URL from viewDocumentAction
 *  contentType  - MIME type (e.g. "application/pdf", "image/png")
 *  fileName     - display name (optional)
 */
const DocumentViewerModal = ({
  isOpen,
  onClose,
  url,
  contentType,
  fileName,
}) => {
  // Revoke blob URL when modal closes
  const handleClose = () => {
    onClose();
  };

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen || !url) return null;

  const isImage = contentType?.startsWith("image/");
  const isPdf = contentType?.includes("pdf");

  const openInNewTab = (e) => {
    e?.stopPropagation();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="dvm-overlay" onClick={handleClose}>
      <div className="dvm-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        {/* <div className="dvm-header">
          <span className="dvm-header__title">
            {fileName || "Document Preview"}
          </span>
          <div className="dvm-header__actions">
            <button
              className="dvm-btn dvm-btn--close"
              onClick={handleClose}
              title="Close"
            >
              ✕
            </button>
          </div>
        </div> */}

        {/* Document viewer */}
        <div className="dvm-body">
          {isPdf && (
            <iframe
              src={url}
              title={fileName || "Document"}
              className="dvm-iframe"
            />
          )}
          {isImage && (
            <div className="dvm-image-wrap">
              <img
                src={url}
                alt={fileName || "Document"}
                className="dvm-image"
              />
            </div>
          )}
          {!isPdf && !isImage && (
            <div className="dvm-unsupported">
              <p>Preview not available for this file type.</p>
              <button className="dvm-btn-open" onClick={openInNewTab}>
                Open in new tab
              </button>
            </div>
          )}
        </div>

        {/* Fullscreen button — bottom right */}
        <button
          type="button"
          className="dvm-fullscreen-btn"
          onClick={openInNewTab}
          title="Open fullscreen in new tab"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default DocumentViewerModal;
